import { useCallback, useEffect, useMemo, useState } from 'react';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { array, InferType, object } from 'yup';

import { RoomReadModel, useGetRoomsQuery, usePutResidentialUnitsByIdRoomsMutation } from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  FormSubmitButton,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  useFormAdd,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { ResidentialUnitRoomItem } from './residential-unit-room-item.component';
import {
  ResidentialUnitRoomFormData,
  ResidentialUnitRoomItemForm,
  residentialUnitRoomSchema,
} from './residential-unit-room-item.form';

import { CancelRequestModal } from '../../../../../../../components/cancel-request-modal/cancel-request-modal.component';

const schema = object({
  rooms: array().of(residentialUnitRoomSchema).required(),
});

type FormValues = InferType<typeof schema>;
type FormDefaultValues = DeepPartial<FormValues>;

export interface ResidentialUnitRoomsCardProps {
  className?: string;
}

export function ResidentialUnitRoomsCard({ className }: ResidentialUnitRoomsCardProps) {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const { spaceId } = useParams<{ spaceId: string }>();

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const [editing, setEditing] = useState<boolean>(false);

  const {
    data: roomsData,
    isFetching,
    isLoading,
  } = useGetRoomsQuery(
    {
      'parentId.Equal': spaceId,
      'pagination.Limit': !editing ? pageSize : undefined,
      'pagination.Offset': !editing ? pageOffset : undefined,
    },
    { skip: !spaceId },
  );

  const [mutation] = usePutResidentialUnitsByIdRoomsMutation();

  const spaceRooms = useMemo(() => roomsData?.items ?? [], [roomsData]);
  const totalCount = roomsData?.totalCount ?? 0;

  const onSubmit = async (data: FormValues) => {
    if (spaceId == null) return;

    try {
      await mutation({
        id: spaceId,
        body: [...data.rooms],
      }).unwrap();
    } catch (e) {
      return;
    }

    setEditing(false);
  };

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      rooms: spaceRooms.map<ResidentialUnitRoomFormData>((data) => ({
        name: data?.name ?? '',
        description: data?.description ?? undefined,
        address: data?.address ?? undefined,
        areaMetric: data?.area?.metric ?? undefined,
        source: data?.source ?? undefined,
        standard: data?.standard ?? undefined,
        type: data?.type ?? 'Bedroom',
        id: data?.id ?? undefined,
      })),
    }),
    [spaceRooms],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    control,
  } = methods;

  /**
   * #########################################
   * Edit Rooms
   */

  const {
    fields: editedSpaceRooms,
    setItems: setRooms,
    appendNewFormItems: appendNewSpaceRoom,
    removeItemByFieldIndex: handleRemoveRoom,
    getItemByFieldIndex: getRoomByFieldIndex,
  } = useFormAdd<ResidentialUnitRoomFormData, RoomReadModel>({
    control,
    name: 'rooms',
    mapItemToFormItem: (x): ResidentialUnitRoomFormData => ({
      id: x.id,
      name: x.name,
      address: x.address,
      type: x.type,
      areaMetric: x.area?.metric,
      description: x.description,
      source: x.source,
      standard: x.standard,
    }),
    itemEqualsFormItem: (a, x) => a.id === x.id,
    formItemEqualsFormItem: (x, y) => x.id === y.id,
  });

  const handleAddNewSpaceRoom = useCallback(() => {
    appendNewSpaceRoom([{ name: '', type: 'Bedroom' }]);
  }, [appendNewSpaceRoom]);

  useEffect(() => {
    setRooms(spaceRooms.map((x) => x));
    reset(defaultValues);
  }, [defaultValues, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * #########################################
   */

  const hasSpaceRooms = spaceRooms != null && spaceRooms.length <= 0;
  const hasEditedSpaceRooms = editedSpaceRooms != null && editedSpaceRooms.length <= 0;

  const isListEmpty = useMemo(
    () => (!editing ? hasSpaceRooms : hasEditedSpaceRooms),
    [editing, hasSpaceRooms, hasEditedSpaceRooms],
  );

  const handleOpenEdit = () => {
    setEditing(true);
    setPage(0);
  };

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isDirty) {
      setCancelingRequest(true);
    } else {
      setEditing(false);
    }
  };

  const handleConfirmCancelRequest = () => {
    setEditing(false);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <CardHeader>
              <CardTitle className="flex-1" variant="dense" loading={!ready}>
                {t('space.rooms')}
              </CardTitle>
              <CardActions>
                {!editing ? (
                  <Button
                    color="primary-dark"
                    disabledVariant="dark"
                    size="sm"
                    onClick={handleOpenEdit}
                    disabled={isFetching || spaceId == null}
                  >
                    {tCommon('edit')}
                  </Button>
                ) : (
                  <>
                    <Button size="sm" color="primary-dark" disabled={isFetching} onClick={handleAddNewSpaceRoom}>
                      {tCommon('add')}
                    </Button>
                    <FormSubmitButton type="submit" size="sm" disabled={isFetching}>
                      {tCommon('save')}
                    </FormSubmitButton>
                    <Button color="primary-dark" size="sm" onClick={checkCancelRequest}>
                      {tCommon('cancel')}
                    </Button>
                  </>
                )}
              </CardActions>
            </CardHeader>

            <CardContent variant="fill">
              <Table className="overflow-hidden rounded-md">
                {!isLoading && !isFetching && isListEmpty && (
                  <caption className="px-4 py-4 [caption-side:bottom]">
                    <EmptyPlaceholder>
                      {ready ? (
                        tCommon('noEntitySpecified', { entity: t('space.rooms') }).toLowerCase()
                      ) : (
                        <Skeleton variant="text" className="w-[40%]" />
                      )}
                    </EmptyPlaceholder>
                  </caption>
                )}

                <TableHead>
                  <TableHeadRow>
                    <TableHeadCell className="w-40">{tGlobal('room.name')}</TableHeadCell>
                    <TableHeadCell className="w-38">{tGlobal('room.type')}</TableHeadCell>
                    <TableHeadCell className="w-38">{tGlobal('room.area')}</TableHeadCell>
                    <TableHeadCell className="w-[30%]">{tGlobal('room.description')}</TableHeadCell>
                    <TableHeadCell className="w-0" />
                  </TableHeadRow>
                </TableHead>

                <TableBody>
                  {(isLoading || (isFetching && isListEmpty)) &&
                    Array.from(Array(3).keys()).map((x) => <ResidentialUnitRoomItem key={x} isFetching />)}

                  {!isLoading &&
                    !editing &&
                    spaceRooms?.map((room, index) => (
                      <ResidentialUnitRoomItem key={room.id} room={room} isFetching={isFetching} />
                    ))}

                  {!isLoading &&
                    editing &&
                    editedSpaceRooms?.map((field, index) => (
                      <ResidentialUnitRoomItemForm
                        key={field.id}
                        fieldIndex={index}
                        room={getRoomByFieldIndex(index)}
                        isFetching={isFetching}
                        onRemove={handleRemoveRoom}
                      />
                    ))}
                </TableBody>
              </Table>

              {!editing && (
                <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
              )}
            </CardContent>
          </form>
        </FormProvider>
      </Card>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />
    </>
  );
}
